import { ResourceProfilePageComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/resource-profile-page/resource-profile-page.component';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MyRequestFormComponent } from '../my-request/my-request-form/my-request-form.component';
import { AssetManagementService } from '../asset-management-service';
import { getMonthDateRange } from '@utils/date.utils';
import { yearDateFormat } from '@constants/dateFormat';
import { formatSingleDate } from '@utils/utils';
import { HiringRequestService } from '@app/pages/hiring-manager/hiring-request/hiring-request.service';

@Injectable({
  providedIn: 'root',
})
export class MyRequestTableService {
  private readonly dataSubject = new BehaviorSubject<any[]>([]);
  public readonly data$ = this.dataSubject.asObservable();
  public readonly animal = signal<any>(null);

  currentStartDate: string = '';
  currentEndDate: string = '';

  // Memoized observables for employee data
  private employeeData$: Observable<any[]>;
  public leaderOptions$: Observable<any[]>;
  public managerOptions$: Observable<any[]>;

  constructor(
    private readonly dialog: MatDialog,
    private readonly assetManagementService: AssetManagementService,
    private readonly hiringRequestService: HiringRequestService,
  ) {
    // Create employee data observable with shareReplay to cache and share result
    this.employeeData$ = this.hiringRequestService.getEmployeeData().pipe(
      map((response) => response.data || []),
      shareReplay(1), // Cache the last emitted value and share with all subscribers
    );

    // Create derived observables for leader and manager options
    this.leaderOptions$ = this.employeeData$.pipe(
      map((employees) =>
        employees
          .filter((e: any) => e.roleType === 'D&T Leader')
          .map((e: any) => ({ label: e.employeeName, value: e.email })),
      ),
    );

    this.managerOptions$ = this.employeeData$.pipe(
      map((employees) =>
        employees
          .filter((e: any) => e.roleType === 'Reporting Manager')
          .map((e: any) => ({ label: e.employeeName, value: e.email })),
      ),
    );

    // Subscribe once to trigger the API call and cache the result
    this.employeeData$.subscribe({
      next: (employees) => {
        console.log(
          '[MyRequestTableService] Employee data loaded and cached:',
          employees.length,
          'employees',
        );
      },
      error: (error) => {
        console.error(
          '[MyRequestTableService] Error loading employee data:',
          error,
        );
      },
    });

    this.leaderOptions$.subscribe({
      next: (leaders) => {
        console.log(
          '[MyRequestTableService] Leader options:',
          leaders.length,
          'leaders',
        );
      },
    });

    this.managerOptions$.subscribe({
      next: (managers) => {
        console.log(
          '[MyRequestTableService] Manager options:',
          managers.length,
          'managers',
        );
      },
    });
  }

  loadInitialData(): void {
    const { startDate, endDate } = getMonthDateRange(
      history.state.selectedMonth,
      yearDateFormat.date,
    );
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.loadAssetRequests(
      formatSingleDate(this.currentStartDate, yearDateFormat.date),
      formatSingleDate(this.currentEndDate, yearDateFormat.date),
    );
  }

  // Load asset requests from API
  loadAssetRequests(startDate: string, endDate: string): void {
    this.assetManagementService.getAssetRequests(startDate, endDate).subscribe({
      next: (response) => {
        this.dataSubject.next(response.data || []);
      },
      error: () => {
        this.dataSubject.next([]);
      },
    });
  }
  
  updateDateRange(startDate: string, endDate: string): void {
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
  }

  // Load email notifications by location

  // Create new request modal
  openCreateRequestModal(): void {
    this.openDialog();
  }

  // Open dialog method
  openDialog(): void {
    const dialogRef: MatDialogRef<MyRequestFormComponent> = this.dialog.open(
      MyRequestFormComponent,
      {
        width: '1000px',
        maxWidth: '90vw',
        minHeight: '600px',
        maxHeight: '90vh',
        disableClose: false,
        panelClass: 'my-request-modal',
        data: {
          isModal: true,
          isEditMode: false,
        },
      },
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result.refreshTable) {
        this.loadAssetRequests(this.currentStartDate, this.currentEndDate);
      }
    });
  }

  // Edit existing request modal
  openEditRequestModal(data: any): void {
    const dialogRef: MatDialogRef<MyRequestFormComponent> = this.dialog.open(
      MyRequestFormComponent,
      {
        width: '1000px',
        maxWidth: '90vw',
        minHeight: '600px',
        maxHeight: '90vh',
        disableClose: false,
        panelClass: 'my-request-modal',
        data: {
          isModal: true,
          isEditMode: true,
          formData: data,
        },
      },
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined) {
        this.loadAssetRequests(this.currentStartDate, this.currentEndDate);
      }
    });
  }

  handleFormSubmission(formData: any): void {
    // TODO: Implement form submission logic
  }

  // Handle create and edit actions only
  handleAction(action: string, data?: any): void {
    console.log('Action received in service:', action, data);
    switch (action) {
      case 'edit':
        if (data) {
          this.openEditRequestModal(data);
        }
        break;
      case 'create':
        this.openCreateRequestModal();
        break;
    }
  }
  // Handler for resource click events from ag-Grid cell renderer
  onResourceClick(data: {
    employeeId: string;
    resourceName: string;
    designation: string;
    firstName?: string;
    lastName?: string;
    rowData?: any;
  }): void {


    // Open Resource Profile Page dialog
    this.dialog.open(ResourceProfilePageComponent, {
      width: '1400px',
      height: '900px',
      panelClass: ['resource-profile-dialog-panel', 'center-dialog'],
      autoFocus: false,
      disableClose: false,
      // panelClass: 'resource-profile-modal',
      data: {
        employeeId: data.employeeId,
      },
    });
  }
}
